import {WorkspaceModelType} from "@stores/WorkspaceStore";
import {ItemType} from "../../API";

import {createDocument} from "../../sync/DocumentApiRequests";
import {getCurrentUser} from "../../utils/user";
import {selectItem} from "../ui/selectItem";
import {ActionName, isAllowed} from "../../access_rights/isAllowed";
import {triggerLimitAlert} from "../../access_rights/triggerLimitAlert";
import {UndoableActionGroup} from "../common/UndoableActionGroup";
import {UndoableAction} from "../common/UndoableAction";
import {deleteItemForever} from "./deleteItemForever";
import {queryGqlApi} from "../../graphql/queryGqlApi";
import {createItem as createItemGQL} from "../../graphql/mutations";
import {itemToItemMSTSnapshotIn} from "@stores/convertors/itemToItemMSTSnapshotIn";
import {CrudServices} from "../../platform/services/types/ServiceDependencies";
import {AppState} from "@stores/AppState";


export const INITIAL_DOC_STATE = {
    type: "doc", content: [{type: "paragraph"}]
};

export class UndoableCreateItem implements UndoableAction {
    private services: CrudServices;
    private options: CreateItemOptions;
    private newItemId?: string;

    constructor(
        services: CrudServices,
        options: CreateItemOptions
    ) {
        this.services = services;
        this.options = options;
    }

    execute(): Promise<any> {
        return _createItem(this.services, this.options).then((values) => {
            const [WS_item, item] = values;
            this.newItemId = WS_item.id;
            return values;
        });
    }

    undo(): Promise<any> {
        if (this.newItemId) {
            return deleteItemForever(
                {
                    ...this.services,
                    appState: this.options.appState,
                },
                this.newItemId,
                this.options.workspace,
                undefined,
                true
            );
        } else {
            return Promise.resolve();
        }
    }
}


export interface CreateItemOptions {
    workspace: WorkspaceModelType,
    appState: AppState,
    type?: ItemType,
    parentId?: string | null,
    name?: string,
    content?: Object | null,
    noSelect?: boolean,
    index?: number,
}

export const createItem = (
    services: CrudServices,
    createItemOptions: CreateItemOptions,
    undoableActionGroup?: UndoableActionGroup
) => {
    if (!isAllowed(createItemOptions.appState.userState!.dsManager!, {
        actionName: ActionName.CreateItem,
        workspace: createItemOptions.workspace
    })) {
        triggerLimitAlert(createItemOptions.appState, ActionName.CreateItem);
        return Promise.resolve([null, null]);
    }

    if (undoableActionGroup !== undefined) {
        const undoableCreateItem = new UndoableCreateItem(services, createItemOptions);
        return undoableActionGroup.add(undoableCreateItem);
    } else {
        return _createItem(services, createItemOptions);
    }
}

async function _createItem(
    services: CrudServices,
    {
        workspace,
        appState,
        type = ItemType.LEAF,
        parentId = null,
        name,
        content = null,
        noSelect = false,
        index = undefined,
    }: CreateItemOptions
) {
    const rootStore = appState.rootStore;
    const {authService, dataClientService} = services;

    const user = await getCurrentUser(authService);
    const item = await queryGqlApi(
        createItemGQL,
        {
            input: {
                ownerId: user!['custom:clientId'],
                childIds: JSON.stringify([]),
                parentIds: JSON.stringify(parentId === null ? [] : [parentId]),
                type: type,
                workspaceId: workspace.id,
                name: name || '',
                readRoles: JSON.stringify([]),
                writeRoles: JSON.stringify([]),
                creator: user!.sub,
                pinned: false,
                trashed: false,
                archived: false,
                updatedAt: (new Date()).toISOString()
            }
        },
        dataClientService,
        authService,
        'main'
    );

    if (type === ItemType.LEAF) {
        await createDocument(
            appState.userState!.wsServerConnection,
            {
                content: content ? content : INITIAL_DOC_STATE,
                itemId: item.id,
                wsId: workspace.id,
            }
        );
    }

    // @ts-ignore
    await workspace.addItem(itemToItemMSTSnapshotIn(item), parentId, noSelect, index);
    if (!noSelect) {
        selectItem(rootStore, item.id);
        workspace.setScrollIntoViewId(item.id);
    }

    const WS_item = workspace.items.get(item.id)!;
    return [WS_item, item];
}
